// Libararies
import _ from 'lodash';
import React from 'react';

// Supermove
import {Icon, DataLoader, Space, Styled} from '@supermove/components';
import {gql} from '@supermove/graphql';
import {useQuery, useNavigationDOM, UrlFiltersType, useState} from '@supermove/hooks';
import {Project, ProjectType, JobModel, ProjectModel} from '@supermove/models';
import {colors} from '@supermove/styles';

// Shared
import EmptyState from '@shared/design/components/EmptyState';
import Line from '@shared/design/components/Line';
import Tabs from '@shared/design/components/Tabs';
import JobDetailsBlock from '@shared/modules/Project/components/Job/JobDetailsBlock';
import ProjectJobBlock from '@shared/modules/Project/components/Job/ProjectJobBlock';
import WidgetBuilder from '@shared/modules/Project/components/widgets/components/WidgetBuilder';
import useProjectLayoutContext, {
  ProjectLayoutContextProvider,
} from '@shared/modules/Project/context/useProjectLayoutContext';

const TabsContainer = Styled.View`
  background-color: ${colors.white};
`;

const BlockContainer = Styled.View`
  background-color: ${colors.white};
`;

const getJobTabs = ({project}: {project: ProjectModel}) => {
  const parentAndChildJobs = _.reduce<JobModel, JobModel[]>(
    project.allJobsExcludingChildJobs.filter((job) => !job.isCancelled),
    (result, job) => [...result, ...(job.isEstimatedRange ? job.jobsForEstimatedRange : [job])],
    [],
  );

  return parentAndChildJobs.map((job) => ({
    key: job.id,
    label: job.shortName,
    jobUuid: job.uuid,
  }));
};

const ProjectJobsWidgetBody = ({
  project,
  jobUuid,
  refetch,
  refetchAndReset,
  handleResetWidget,
  handleGoToBilling,
}: {
  project: ProjectModel;
  jobUuid: string;
  refetch: () => void;
  refetchAndReset: () => void;
  handleResetWidget: () => void;
  handleGoToBilling: () => void;
}) => {
  const {setProjectBlockKindToHeight} = useProjectLayoutContext();

  return (
    <React.Fragment>
      {ProjectType.getJobBlocks(project.projectType).map((projectBlockKind, index) => (
        <BlockContainer
          key={projectBlockKind}
          onLayout={(e) => {
            setProjectBlockKindToHeight((stateBlocks: {[key: string]: number}) => ({
              ...stateBlocks,
              [projectBlockKind]: e.nativeEvent.layout.height,
            }));
          }}
        >
          <React.Fragment>
            {index > 0 && <Line style={{height: 4}} />}
            <ProjectJobBlock
              index={index}
              project={project}
              jobUuid={jobUuid}
              projectBlockKind={projectBlockKind}
              pageRefetch={refetch}
              refetchAndReset={refetchAndReset}
              handleResetWidget={handleResetWidget}
              handleGoToBilling={handleGoToBilling}
              isWidget
            />
          </React.Fragment>
        </BlockContainer>
      ))}
    </React.Fragment>
  );
};

export type ProjectJobsFiltersType = {
  jobUuid?: string;
  block?: string;
};
export type ProjectJobsUrlFiltersType = UrlFiltersType<ProjectJobsFiltersType>;
export const PROJECT_JOBS_FILTERS_KEYS: (keyof ProjectJobsFiltersType)[] = ['jobUuid', 'block'];

const ProjectJobsWidgetData = ({
  projectUuid,
  jobUuid,
  handleUpdateJob,
  handleResetJob,
  handleResetWidget,
  handleGoToBilling,
}: {
  projectUuid: string;
  jobUuid?: string;
  handleUpdateJob: (jobUuid: string) => void;
  handleResetJob: () => void;
  handleResetWidget: () => void;
  handleGoToBilling: () => void;
}) => {
  const {navigator} = useNavigationDOM();
  const {loading, data, refetch} = useQuery(ProjectJobsWidget.query, {
    fetchPolicy: 'cache-and-network',
    variables: {
      projectUuid,
    },
  });

  return (
    <WidgetBuilder.ContentContainer>
      <DataLoader loading={loading} data={data} as={WidgetBuilder.Loader}>
        {({loadedData}) => {
          const {project} = loadedData;
          const tabs = getJobTabs({project});

          return (
            <React.Fragment>
              {_.isEmpty(tabs) ? (
                <EmptyState.ContentContainer>
                  <EmptyState
                    icon={Icon.Search}
                    title={'No jobs.'}
                    message={
                      'There are no jobs on this project.\nAdd a job and it will appear here.'
                    }
                    primaryActionIcon={Icon.Plus}
                    primaryActionText={'Add Job'}
                    handlePrimaryAction={() =>
                      // TODO(dan) For now this is only available on office app. Need to make this handle
                      // react navigation to make it compatible with other apps.
                      navigator.push(
                        `/projects/${projectUuid}/edit/jobs?action=${Project.JOB_ACTIONS.ADD}`,
                      )
                    }
                  />
                </EmptyState.ContentContainer>
              ) : (
                <React.Fragment>
                  <TabsContainer>
                    <Space height={16} />
                    <Tabs
                      tabs={tabs}
                      handlePressTab={({jobUuid}) => handleUpdateJob(jobUuid)}
                      activeTabIndex={
                        jobUuid ? tabs.findIndex((tab) => tab.jobUuid === jobUuid) : 0
                      }
                      style={{paddingLeft: 16}}
                      tabStyle={{paddingLeft: 4, paddingRight: 4}}
                    />
                  </TabsContainer>
                  <Line />
                  <ProjectJobsWidgetBody
                    project={project}
                    jobUuid={jobUuid || tabs[0].jobUuid}
                    refetch={refetch}
                    refetchAndReset={() => {
                      refetch();
                      handleResetJob();
                    }}
                    handleResetWidget={handleResetWidget}
                    handleGoToBilling={handleGoToBilling}
                  />
                  <Space height={48} style={{backgroundColor: colors.white, zIndex: -1}} />
                </React.Fragment>
              )}
            </React.Fragment>
          );
        }}
      </DataLoader>
    </WidgetBuilder.ContentContainer>
  );
};

const ProjectJobsWidget = ({
  projectUuid,
  jobUuid,
  handleUpdateJob,
  handleResetWidget,
  handleGoToBilling,
}: {
  projectUuid: string;
  jobUuid?: string;
  handleUpdateJob: (jobUuid: string) => void;
  handleResetWidget: () => void;
  handleGoToBilling: () => void;
}) => {
  const [resetKey, setResetKey] = useState(0);

  return (
    <ProjectLayoutContextProvider isWidget>
      <ProjectJobsWidgetData
        key={resetKey}
        projectUuid={projectUuid}
        jobUuid={jobUuid}
        handleUpdateJob={handleUpdateJob}
        handleResetJob={() => setResetKey((prevKey) => prevKey + 1)}
        handleResetWidget={handleResetWidget}
        handleGoToBilling={handleGoToBilling}
      />
    </ProjectLayoutContextProvider>
  );
};

// --------------------------------------------------
// Data
// --------------------------------------------------
ProjectJobsWidget.query = gql`
  ${ProjectJobBlock.fragment}
  ${ProjectType.getJobBlocks.fragment}
  ${JobDetailsBlock.fragment}

  query ProjectJobsWidget($projectUuid: String!) {
    ${gql.query}
    project(uuid: $projectUuid) {
      id
      allJobsExcludingChildJobs {
        id
        uuid
        shortName
        isEstimatedRange
        isCancelled
        jobsForEstimatedRange {
          id
          uuid
          shortName
        }
      }
      projectType {
        id
        ...ProjectType_getJobBlocks
      }
      ...JobDetailsBlock
      ...ProjectJobBlock
    }
  }
`;

export default ProjectJobsWidget;
